import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Grid, Link, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import FileViewer from 'react-file-viewer';
import { getCandidateResumeByFileUrl, getPresignedURLForGet, ICandidate } from 'wisestep-api';
import {
  Item,
  WsColors,
  WsTypography,
  ScrollableTabs,
  WsTypoVariants,
  getSearchRegExp,
} from 'wisestep-base';

import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { downloadCV, filterS3Url, trackCV } from '../utils/cvHelper';

import { InformationRequests } from './InformationRequests';
import PdfViewer from './PdfViewer';

const IFrame = styled('iframe')(() => ({
  height: '100%',
  width: '100%',
  border: 0,
  overflowY: 'auto',
}));

interface ISubmissionResumeView {
  resumeLocation?: string;
  isOriginaFormatView?: boolean;
  resumeName?: string;
  changeView: (view: boolean) => void;
  candidate: ICandidate;
}

const SubmissionResumeView: FC<ISubmissionResumeView> = ({
  resumeLocation,
  isOriginaFormatView,
  resumeName,
  changeView,
  candidate,
}) => {
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [originalView, setOriginalView] = useState(
    !isEmpty(searchQuery) ? false : isOriginaFormatView
  );
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [isResumeLoaded, setIsResumeLoaded] = useState(false);
  const [viewChange, setViewChange] = useState(false);
  const [url, setUrl] = useState<{ url: string; type: string; resumeContent?: string } | null>(
    null
  );
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);

  const [regex] = useState<RegExp | undefined>(getSearchRegExp(searchQuery) ?? undefined);
  useEffect(() => {
    if (viewChange) {
      setOriginalView(isOriginaFormatView);
    }
  }, [isOriginaFormatView, viewChange]);

  useEffect(() => {
    const getHighLightKeyWordResume = async (url: string) => {
      if (searchQuery) {
        try {
          const response = await getCandidateResumeByFileUrl(url);
          if (
            response.data.content &&
            response.data.content !== 'content not found' &&
            response.data.content !== 'NO CONTENT' &&
            response.data.content !== 'NO_CONTENT' &&
            response.data.content !== 'EXTRACTION_FAILED'
          ) {
            return {
              url: response.data.resumeLocation ? response.data.resumeLocation : '',
              type: 'content',
              resumeContent: response.data.content.replace(/\n/g, '<br>'),
            };
          }
          return await filterS3Url(url);
        } catch (error) {
          return await filterS3Url(url);
        }
      }
      return await filterS3Url(url);
    };
    (async () => {
      if (
        !isEmpty(resumeLocation) &&
        (resumeLocation?.split('.').pop() === 'doc' || !originalView)
      ) {
        const getURL = await getHighLightKeyWordResume(resumeLocation ?? '');
        setUrl(getURL);
        setOriginalView(false);
        setIsResumeLoaded(true);
        trackCV('view', resumeLocation, cvViewSourcePage);
      } else if (!isEmpty(resumeLocation)) {
        trackCV('view', resumeLocation, cvViewSourcePage);
      } else {
        setIsResumeLoaded(true);
      }
    })();
  }, [originalView, resumeLocation, searchQuery, cvViewSourcePage]);

  const [resumeUrl, setResumeUrl] = useState('');
  useEffect(() => {
    const getUrl = async () => {
      try {
        const data = await getPresignedURLForGet({
          fileName: resumeLocation ?? '',
        });
        setResumeUrl(data.data.preSignedUrl);
        setIsResumeLoaded(true);
      } catch (e) {
        setIsResumeLoaded(true);
      }
    };
    getUrl();
  }, [resumeLocation]);

  const changeResumeView = () => {
    if (resumeLocation?.split('.').pop() === 'doc' && originalView === false) {
      setNotification(
        warning(".DOC file view is not supported. Please download to view .DOC format CV's")
      );
    } else {
      setViewChange(true);
      changeView(!originalView);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={10} mb={1.5}>
          <ScrollableTabs
            scrollList={[
              { tabName: 'Profile' },
              { tabName: 'Documents' },
              { tabName: 'Questionnaires' },
              { tabName: 'Information' },
            ]}
            getTabIndex={(index: number) => setTabIndex(index)}
          />
        </Grid>
        <Grid item xs={2} mb={1.5}>
          <Stack direction="row">
            <Tooltip title={originalView ? 'View in HTML format' : 'View in original format'}>
              <VisibilityIcon
                onClick={changeResumeView}
                sx={{
                  mt: 1.1,
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: originalView ? WsColors.TheBlues500 : WsColors.GenericRichBlack,
                }}
              />
            </Tooltip>
            <Box sx={{ mr: 1 }}></Box>
            <DownloadIcon
              onClick={() => downloadCV(resumeLocation ?? '', resumeName)}
              sx={{ mt: 1.2, fontSize: '30px', cursor: 'pointer' }}
            />
          </Stack>
        </Grid>
      </Grid>
      <Item
        sx={{
          height: '100%',
          bgcolor: WsColors.TertiaryShades200,
          marginRight: 1,
          pt: 1,
          pr: 0.2,
          pl: 1,
        }}
      >
        <>
          {tabIndex === 0 && (
            <>
              {isResumeLoaded ? (
                <>
                  {url || (resumeLocation && resumeLocation?.split('.').pop() != 'doc') ? (
                    originalView ? (
                      <>
                        {url && url.type == 'image' ? (
                          <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                            <img
                              id="imageviewer"
                              src={url.url}
                              style={{ width: '100%' }}
                              alt="CV View Error"
                            />
                          </Item>
                        ) : resumeUrl && resumeLocation?.split('.').pop() === 'pdf' ? (
                          <PdfViewer url={resumeUrl} highlightRegexp={regex} />
                        ) : (
                          <>
                            {resumeUrl && resumeLocation?.split('.').pop() === 'docx' ? (
                              <FileViewer fileType="docx" filePath={resumeUrl} />
                            ) : (
                              <>
                                {url &&
                                url.type == 'content' &&
                                resumeLocation &&
                                resumeLocation?.split('.').pop() === 'doc' ? (
                                  <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                                    <Box
                                      sx={{
                                        height: '100%',
                                        bgcolor: WsColors.White,
                                        marginRight: 1,
                                      }}
                                    >
                                      <Typography
                                        height="100%"
                                        sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                                        dangerouslySetInnerHTML={{
                                          __html: url.resumeContent
                                            ? highlightKeys(
                                                url.resumeContent.replace(
                                                  /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
                                                  'illegalscript'
                                                ),
                                                searchQuery ?? ''
                                              )
                                            : '',
                                        }}
                                      />
                                    </Box>
                                  </Item>
                                ) : (
                                  <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                                    <Box textAlign="center">
                                      <WsTypography variant={WsTypoVariants.TITLE}>
                                        File format not supported to view,
                                      </WsTypography>
                                      <Link
                                        onClick={() => downloadCV(resumeLocation ?? '', resumeName)}
                                      >
                                        Click here to download cv
                                      </Link>
                                    </Box>
                                  </Item>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {url && url.type == 'image' ? (
                          <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                            <img
                              id="imageviewer"
                              src={url.url}
                              style={{ width: '100%' }}
                              alt="CV View Error"
                            />
                          </Item>
                        ) : url && url.type == 'content' ? (
                          <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                            <Box sx={{ height: '100%', bgcolor: WsColors.White, marginRight: 1 }}>
                              <Typography
                                height="100%"
                                sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                                dangerouslySetInnerHTML={{
                                  __html: url.resumeContent
                                    ? highlightKeys(
                                        url.resumeContent.replace(
                                          /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
                                          'illegalscript'
                                        ),
                                        searchQuery ?? ''
                                      )
                                    : '',
                                }}
                              />
                            </Box>
                          </Item>
                        ) : (
                          <>
                            {url && (
                              <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                                <IFrame id="imageviewer" title="imageviewer" src={url.url} />
                              </Item>
                            )}
                          </>
                        )}
                      </>
                    )
                  ) : (
                    <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" mt={10}>
                        <WsTypography>
                          {resumeLocation && resumeLocation.indexOf('.doc') > -1
                            ? '.doc format file view are not supported in browser. Please download the file to view it locally'
                            : 'CV Not Available'}
                        </WsTypography>
                      </Stack>
                    </Item>
                  )}
                </>
              ) : (
                <Item sx={{ height: '100%', bgcolor: WsColors.White }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" mt={10}>
                    <WsTypography>Loading CV...</WsTypography>
                  </Stack>
                </Item>
              )}
            </>
          )}
          {(tabIndex === 1 || tabIndex === 2) && (
            <Stack direction="row" justifyContent="center" alignItems="center" mt={10}>
              <WsTypography>Coming soon</WsTypography>
            </Stack>
          )}
          {tabIndex === 3 && <InformationRequests candidate={candidate} />}
        </>
      </Item>
    </>
  );
};

export default SubmissionResumeView;

const getRegexPattern = (keywordArray: string[]) => {
  const pattern = '(\\b)(' + keywordArray.join('|') + ')(\\b)';
  return new RegExp(pattern, 'gi');
};

const splitMulti = (str: string, separators: string[]) => {
  const tempChar = 't3mp';
  const regExForExactMatch = /"([^"]+)"|'([^']+)'/g;
  let exactMatchList: string[] = [];
  if (str.match(regExForExactMatch)) {
    exactMatchList =
      str && str.match(regExForExactMatch)
        ? str.match(regExForExactMatch)?.map(function (s) {
            return s.substring(1, s.length - 1);
          }) ?? []
        : [];
  }
  const re = new RegExp('/' + separators.join('|') + '/', 'gi');
  const chars = str.replaceAll(regExForExactMatch, '').replace(re, tempChar).split(tempChar);
  const splitKeywords: string[] = [];

  exactMatchList.map((keyWord) =>
    keyWord
      ?.split(' ')
      ?.filter((el) => el.length > 0)
      .map((el) => splitKeywords.push(el?.trim()))
  );
  return exactMatchList
    .concat(splitKeywords)
    .concat(chars.map((el) => el.trim()).filter((el) => el.length > 0));
};

const highlightKeys = (textToHighlight: string, keywords: string) => {
  let filteredText = textToHighlight;
  if (keywords) {
    keywords = keywords.replace(/[()+*]/g, ' ');
    const keywordList = splitMulti(keywords, ['and', 'or', 'AND', 'OR', '\\(', '\\)']);
    const pattern = getRegexPattern(keywordList);
    filteredText = textToHighlight.replaceAll(pattern, '<em>$2</em>');
  }
  return filteredText;
};
